import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPlaybackToken } from '../net/spotify-auth.ts';
import { readSetting, writeSetting } from '../storage.ts';

/**
 * Bindet das Spotify Web Playback SDK ein und macht aus diesem Browser-Tab
 * ein Spotify-Abspielgeraet.
 *
 * Wichtige Rahmenbedingungen (Spotify-Vorgaben, nicht aenderbar):
 *  - Nur mit Spotify Premium.
 *  - Funktioniert NICHT in mobilen Browsern. Handys sind Controller,
 *    keine Lautsprecher.
 *  - Ton darf erst nach einer echten Nutzerinteraktion starten. Deshalb
 *    muss `enable()` aus einem Klick-Handler heraus aufgerufen werden --
 *    dort rufen wir `activateElement()`.
 *  - Das Geraet gehoert dem angemeldeten Konto. Fuer Remote-Play muss
 *    also jeder Lautsprecher sein EIGENES Spotify-Konto verbinden.
 */

// ---------------------------------------------------------------------------
// Typen des SDK (es liefert keine eigenen Typdefinitionen mit)
// ---------------------------------------------------------------------------

interface SpotifyPlayerInstance {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  activateElement: () => Promise<void>;
  addListener: (event: string, callback: (payload: never) => void) => boolean;
  removeListener: (event: string) => boolean;
  getCurrentState: () => Promise<unknown>;
  setVolume: (volume: number) => Promise<void>;
}

interface SpotifyPlayerConstructor {
  new (options: {
    name: string;
    getOAuthToken: (callback: (token: string) => void) => void;
    volume?: number;
  }): SpotifyPlayerInstance;
}

declare global {
  interface Window {
    Spotify?: { Player: SpotifyPlayerConstructor };
    onSpotifyWebPlaybackSDKReady?: () => void;
  }
}

const SDK_URL = 'https://sdk.scdn.co/spotify-player.js';

/** Laedt das SDK-Script genau einmal und wartet auf seinen Ready-Callback. */
let sdkPromise: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve, reject) => {
    if (window.Spotify?.Player) {
      resolve();
      return;
    }

    // Das SDK ruft diesen globalen Callback auf, sobald es bereit ist.
    window.onSpotifyWebPlaybackSDKReady = () => resolve();

    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error('sdk_load_failed'));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
}

/**
 * Erkennt mobile Browser. Dort verweigert das SDK die Arbeit, also
 * blenden wir die Lautsprecher-Option gleich aus statt sie scheitern
 * zu lassen.
 */
export function isMobileBrowser(): boolean {
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  // iPadOS meldet sich als Mac; Touch-Punkte verraten es.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export type PlayerStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unsupported';

export interface SpotifyPlayerApi {
  status: PlayerStatus;
  deviceId: string | null;
  errorMessage: string | null;
  /** Muss aus einem Klick-Handler kommen (Autoplay-Richtlinie). */
  enable: () => Promise<void>;
  disable: () => void;
  /** Lautstaerke zwischen 0 und 1. */
  volume: number;
  setVolume: (volume: number) => void;
}

const VOLUME_KEY = 'hitster.volume';
const DEFAULT_VOLUME = 0.8;

/** Gemerkte Lautstaerke, damit sie einen Reload uebersteht. */
function readStoredVolume(): number {
  const raw = readSetting(VOLUME_KEY);
  if (raw === null) return DEFAULT_VOLUME;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return DEFAULT_VOLUME;
  return Math.min(1, Math.max(0, parsed));
}

export interface UseSpotifyPlayerOptions {
  /** Wird gerufen, sobald eine Device-ID vorliegt. */
  onReady: (deviceId: string) => void;
  /** Wird gerufen, wenn das Geraet verschwindet. */
  onGone: () => void;
  /** Anzeigename in der Spotify-Geraeteliste. */
  deviceName?: string;
}

export function useSpotifyPlayer(options: UseSpotifyPlayerOptions): SpotifyPlayerApi {
  const { onReady, onGone, deviceName = 'Hitster Digital' } = options;

  const [status, setStatus] = useState<PlayerStatus>(() =>
    isMobileBrowser() ? 'unsupported' : 'idle',
  );
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [volume, setVolumeState] = useState<number>(readStoredVolume);

  const playerRef = useRef<SpotifyPlayerInstance | null>(null);
  // Damit `enable()` die aktuelle Lautstaerke kennt, ohne davon
  // abzuhaengen (sonst wuerde der Player bei jedem Regler-Zug neu gebaut).
  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  // Callbacks in Refs, damit ein neu erzeugter Handler nicht den
  // ganzen Player neu aufbaut.
  const onReadyRef = useRef(onReady);
  const onGoneRef = useRef(onGone);
  onReadyRef.current = onReady;
  onGoneRef.current = onGone;

  const disable = useCallback(() => {
    playerRef.current?.disconnect();
    playerRef.current = null;
    setDeviceId(null);
    setStatus(isMobileBrowser() ? 'unsupported' : 'idle');
    onGoneRef.current();
  }, []);

  const enable = useCallback(async () => {
    if (isMobileBrowser()) {
      setStatus('unsupported');
      return;
    }
    /**
     * Laeuft das SDK schon, nur erneut anmelden statt nichts zu tun.
     *
     * Sonst bleibt der Knopf wirkungslos, wenn der Server die
     * Lautsprecher-Rolle verloren hat, das SDK-Geraet aber noch
     * existiert -- genau das passierte beim Wechsel zwischen
     * Mitspielen und Zuschauen.
     */
    if (playerRef.current) {
      if (deviceId) onReadyRef.current(deviceId);
      return;
    }

    setStatus('loading');
    setErrorMessage(null);

    try {
      await loadSdk();

      const Constructor = window.Spotify?.Player;
      if (!Constructor) throw new Error('sdk_unavailable');

      const player = new Constructor({
        name: deviceName,
        // Das SDK holt sich hierueber selbststaendig frische Tokens.
        getOAuthToken: (callback) => {
          void fetchPlaybackToken()
            .then(callback)
            .catch(() => {
              setStatus('error');
              setErrorMessage('token_unavailable');
            });
        },
        volume: volumeRef.current,
      });

      playerRef.current = player;

      player.addListener('ready', ((payload: { device_id: string }) => {
        setDeviceId(payload.device_id);
        setStatus('ready');
        onReadyRef.current(payload.device_id);
      }) as (payload: never) => void);

      player.addListener('not_ready', (() => {
        // Tab wurde in den Hintergrund geschickt oder Netz weg.
        setStatus('idle');
        setDeviceId(null);
        onGoneRef.current();
      }) as (payload: never) => void);

      const fail = (code: string) =>
        ((payload: { message?: string }) => {
          console.error(`[spotify] ${code}:`, payload?.message);
          setStatus('error');
          setErrorMessage(payload?.message ?? code);
        }) as (payload: never) => void;

      player.addListener('initialization_error', fail('initialization_error'));
      player.addListener('authentication_error', fail('authentication_error'));
      // Kommt praktisch immer, wenn das Konto kein Premium hat.
      player.addListener('account_error', fail('account_error'));
      player.addListener('playback_error', fail('playback_error'));

      const connected = await player.connect();
      if (!connected) throw new Error('connect_rejected');

      // Ohne activateElement bleibt der Ton auf vielen Browsern stumm.
      // Klappt nur, wenn wir aus einem Nutzer-Klick kommen.
      try {
        await player.activateElement();
      } catch (error) {
        console.warn('[spotify] activateElement fehlgeschlagen:', error);
      }
    } catch (error) {
      console.error('[spotify] Player-Start fehlgeschlagen:', error);
      playerRef.current?.disconnect();
      playerRef.current = null;
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  }, [deviceName, deviceId]);

  const setVolume = useCallback((next: number) => {
    const clamped = Math.min(1, Math.max(0, next));
    setVolumeState(clamped);

    writeSetting(VOLUME_KEY, String(clamped));

    // Laeuft der Player schon, sofort uebernehmen.
    void playerRef.current?.setVolume(clamped).catch((error: unknown) => {
      console.warn('[spotify] Lautstaerke konnte nicht gesetzt werden:', error);
    });
  }, []);

  /**
   * Beim Verlassen der Seite saubermachen.
   *
   * ACHTUNG: Dieser Hook darf nur an einer Stelle leben, die die ganze
   * Sitzung ueberdauert. Wird er in einer Ansicht gerendert, die man
   * verlassen kann, reisst dieses Cleanup beim Ansichtswechsel das
   * Spotify-Geraet ab und die Musik verstummt.
   */
  useEffect(() => {
    return () => {
      playerRef.current?.disconnect();
      playerRef.current = null;
    };
  }, []);

  return { status, deviceId, errorMessage, enable, disable, volume, setVolume };
}
